import { createMachine, sendParent } from 'xstate'
import { assign } from '@xstate/immer'
import * as Types from './configSyncMachineTypes'
import { SyncEntry, SYNC_ENTRY_STATUS, SYNC_ENTRY_REQUEST_TYPE } from 'src/classes/CRM/ISyncManifest'
import { CRMIntegration } from '@alucio/aws-beacon-amplify/src/models'
import { Config, TableEnum } from 'src/classes/CRM/CRMIndexedDBTypes'
import { Singleton as IndexDbCrm } from 'src/classes/CRM/CRMIndexedDB'
import { CRMSyncStatus, SET_CONTEXT_VARS } from './crmMachineTypes'
import * as logger from 'src/utils/logger'
import { CRM_LAST_SYNC_AT, CRMUtil } from './util'
import { ERR_CLEANING_FORM_DB, ERR_SYNCING_CONFIG_FORM } from './constants'
import { CRMHandler } from 'src/classes/CRM/CRMHandler';

const CRMDB = IndexDbCrm;
const ERR_INVALID_SESSION_ID = 'INVALID_SESSION_ID';

const getCRMSyncer = (config: CRMIntegration) => {
  return CRMHandler(config).Syncer();
}

export { ERR_CLEANING_FORM_DB, ERR_SYNCING_CONFIG_FORM }

// create a machine with the following configuration:
const configSyncMachine = (config: CRMIntegration) => createMachine<
  Types.crmConfigFormContext,
  Types.crmConfigFormsEvents,
  Types.crmConfigFormsState
>({
  id: 'configSync',
  initial: 'idle',
  predictableActionArguments: true,
  context: {
    hasBeenInitialized: false,
    // consider using a factory function to create the crmSync object
    crmSync: getCRMSyncer(config),
    pendingsEntries: [] as SyncEntry[],
    isFirstTimeSync: false,
  },
  states: {
    idle: {
      description: 'The machine is idle',
      on: {
        'START': [
          { target: 'setContextVars' },
        ],
      },
    },
    checkForPendingEntry: {
      description: 'check for pending entry',
      invoke: {
        id: 'checkForPendingEntry',
        src: 'checkForPendingEntry',
        onDone: [
          { target: 'processEntry', actions: 'setPendingEntries', cond: 'checkForPendingEntry' },
          { target: 'callConfigSyncComplete' },
        ],
        onError: { target: 'cleanFormDB', actions: 'logError' },
      },
    },
    initialize: {
      description: 'load manifest entries for tables from config',
      invoke: {
        src: 'initialize',
        onDone: { target: 'checkForPendingEntry', actions: 'setPendingEntries' },
        onError: { target: 'cleanFormDB', actions: 'logError' },
      },
    },
    setContextVars: {
      description: 'set context variables',
      invoke: {
        src: 'setContextVars',
        onDone: [
          { target: 'initialize', cond: 'isNotInitialized', actions: 'setContextVars' },
          { target: 'checkForPendingEntry', actions: 'setContextVars' },
        ],
        onError: { target: 'error', actions: 'logError' },
      },
    },
    processEntry: {
      description: 'process pending sync entry',
      onEntry: 'setParentSyncingStatus',
      invoke: {
        src: 'processEntry',
        onDone: [
          { target: 'checkForPendingEntry', cond: 'checkForPendingEntry' },
          { target: 'callConfigSyncComplete' },
        ],
        onError: { target: 'cleanFormDB', actions: 'logError' },
      },
    },
    callConfigSyncComplete: {
      description: 'call configSyncComplete',
      onEntry: 'setParentSyncingStatus',
      invoke: {
        id: 'configSyncComplete',
        src: 'configSyncComplete',
        onDone: { target: 'idle', actions: ['cleanUp', 'setParentFinishedSyncedStatus'] },
        onError: { target: 'cleanFormDB', actions: 'logError' },
      },
    },
    cleanFormDB: {
      description: 'clean form db',
      invoke: {
        id: 'cleanFormDB',
        src: 'cleanFormDB',
        onDone: { target: 'error', actions: 'logError' },
        onError: { target: 'onClearFormDB', actions: 'logError' },
      },
    },
    error: {
      onEntry: sendParent((_, event) => {
        const evt = event as any;
        const errorMessage = Array.isArray(evt.data?.errors) ? evt.data?.errors[0]?.message : undefined;
        const err = evt.data.type === ERR_INVALID_SESSION_ID ? ERR_INVALID_SESSION_ID : ERR_SYNCING_CONFIG_FORM
        return {
          type: 'SET_FORM_SYNC_ERROR_STATUS',
          payload: { error: err, status: CRMSyncStatus.ERROR, message: errorMessage },
        }
      }),
      description: 'error',
      type: 'final',
    },
    onClearFormDB: {
      onEntry: sendParent(() => ({
        type: 'SET_FORM_SYNC_ERROR_STATUS',
        payload: { error: ERR_CLEANING_FORM_DB, status: CRMSyncStatus.ERROR },
      })),
      description: 'on error when clearing form db',
      type: 'final',
    },
  },
}, {
  guards: {
    isNotInitialized: (context) => !context.hasBeenInitialized,
    checkForPendingEntry: (context) => context.pendingsEntries.length > 0,
  },
  services: {
    configSyncComplete: async (context) => {
      const syncEntries = await CRMDB.getAll<SyncEntry>('FORM_MANIFEST')
      const config = await context.crmSync.configSyncComplete(syncEntries)
      await CRMDB.upsert(TableEnum.FORM_SETTINGS, config)

      const accountsEntry = syncEntries.find(({ subType, status }) =>
        subType === SYNC_ENTRY_REQUEST_TYPE.ACCOUNTS && status === SYNC_ENTRY_STATUS.SYNCED);
      const crmUserId = CRMUtil.getAuthInformation()?.userInfo?.id;

      if (accountsEntry && crmUserId) {
        logger.CRMMachine.debug('Upserting accounts on IDB...');
        await CRMDB.upsert(TableEnum.ACCOUNT, accountsEntry.data);
        logger.CRMMachine.debug('Upserting crm config on IDB...');
        const configItem = await CRMDB.getById<Config>(TableEnum.CONFIG_OBJECT, crmUserId)
        await CRMDB.upsert(TableEnum.CONFIG_OBJECT, { ...(configItem ?? {}), lastSyncTime: new Date().getTime() })
      }
    },
    initialize: async (context) => {
      const pendingsEntries = context.crmSync.initialize()
      return await Promise.all(pendingsEntries.map((entry) => CRMDB.addSyncEntry(entry)))
    },
    cleanFormDB: async (_, event) => {
      const evt = event as unknown as { data: { type: string } }
      // IF IT HAS ALREADY SYNCED BEFORE, WE DON'T WANT TO DELETE PREVIOUS USABLE DATA
      const isFirstSync = !localStorage.getItem(CRM_LAST_SYNC_AT);
      if (isFirstSync) {
        await Promise.all([CRMDB.clearTable(TableEnum.FORM_SETTINGS), CRMDB.clearTable(TableEnum.FORM_MANIFEST)]);
      }
      return evt?.data ?? event
    },
    checkForPendingEntry: async () => {
      return await CRMDB.getEntryByStatus(SYNC_ENTRY_STATUS.PENDING)
    },
    processEntry: async (context) => {
      const entry = context.pendingsEntries?.[0]
      if (entry) {
        const entries = await context.crmSync.processManifestEntry(entry)
        await Promise.all(entries.map((entry) => CRMDB.addSyncEntry(entry)))
      }
    },
    setContextVars: async () => {
      const formSettings = await CRMDB.getAll<TableEnum.FORM_SETTINGS>('FORM_SETTINGS')
      return {
        isFirstTimeSync: formSettings.length === 0,
      }
    },
  },
  actions: {
    logError: assign((context, event) => {
      context.error = event
      console.error('error', event)
      logger.CRMConfigMachine.error('error', event)
    }),
    cleanUp: assign((context) => {
      context.error = undefined
      context.hasBeenInitialized = false
    }),
    setPendingEntries: assign((context, event) => {
      const { data } = event as unknown as { data: SyncEntry[] | undefined}
      context.pendingsEntries = data || []
    }),
    setContextVars: assign((context, event) => {
      const { data } = event as unknown as SET_CONTEXT_VARS
      context.isFirstTimeSync = data?.isFirstTimeSync || false
      context.hasBeenInitialized = true
    }),
    setParentSyncingStatus: sendParent((context) => {
      // if there are no pending entries, then we are done syncing
      // this case woul be handled by the setParentFinishedSyncedStatus action
      if (context.pendingsEntries.length === 0) {
        return {
          type: '',
          data: {},
        }
      }

      return {
        type: 'SET_FORM_SYNC_STATUS',
        data: {
          status:
            context.isFirstTimeSync
              ? CRMSyncStatus.SYNCING
              : CRMSyncStatus.RE_SYNCING,
        },
      }
    }),
    setParentFinishedSyncedStatus: sendParent(() => {
      return {
        type: 'SET_FORM_SYNC_STATUS',
        data: {
          status: CRMSyncStatus.SYNCED,
        },
      }
    }),
  },
})

export default configSyncMachine
